// @flow
import * as React from 'react';

import memoize from 'memoize-one';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { compose } from 'redux';
import { createSelector } from 'reselect';
import { superpathjoin } from 'superpathjoin';

import { selectHelpdeskUrl } from '@gandi/authentication';
import { selectFromConfig } from '@gandi/redux-config';

import {
  type TranslatorProps,
  withTranslator,
  localeSelector,
} from '@gandi/translate';
import { Base, Cell, Fqdn, Text } from '@gandi/ui';

import CellRowGroup from '../../DomainTransfer/components/DomainList/CellRowGroup';
import type { FullState } from '../../types';

import type { DomainStatus } from '../types';

import styles from './DomainRow.scss';

type OwnProps = {
  domainStatus: DomainStatus,
  fqdn: string,
  notAtGandi: boolean,
  translator: TranslatorProps,
};

type InjectedProps = {
  getFallbackMessage: DomainStatus => string,
  whoisURL: string,
  supportURL: string,
};

type Props = { ...InjectedProps, ...OwnProps };

function getFallbackMessages(__: TranslatorProps['__']) {
  const fallbackMessages = {
    unavailable: __('is not renewable.'),
    transferable: __('is not registered with Gandi.'),
    untransferable: __('is not registered with Gandi.'),
    registerable: __('is available for registration.'),
    invalid: __('is not a valid domain.'),
    error: __('Something seems broken right now,'),
  };

  return (domainStatus: DomainStatus) => fallbackMessages[domainStatus];
}

const memoizedFallbackMessages = memoize(getFallbackMessages);

class FallbackRow extends React.PureComponent<Props> {
  renderActions(): Array<React.Node> {
    const {
      translator: { __ },
      domainStatus,
      fqdn,
      notAtGandi,
    } = this.props;

    const actions: Array<ExactReactElement_DEPRECATED<typeof Link | 'a'>> = [];

    if (domainStatus === 'registerable') {
      actions.push(
        <Link to={`/domain/suggest?search=${fqdn}`}>{__('Register')}</Link>,
      );
    }

    if (notAtGandi || domainStatus === 'unavailable') {
      const whoisURL = new URL(this.props.whoisURL);
      whoisURL.searchParams.set('search', fqdn);
      actions.push(<a href={whoisURL.href}>{__('See WHOIS')}</a>);
    }

    if (domainStatus === 'transferable') {
      actions.push(
        <Link to={`/domain/transfer?search=${fqdn}`}>
          {__('Is it yours? Transfer it')}
        </Link>,
      );
    }

    if (['invalid', 'unavailable'].indexOf(domainStatus) > -1) {
      actions.push(
        <Link to={`/domain/suggest?search=${fqdn}`}>
          {__('Find domains similar to %(term)s', { term: fqdn })}
        </Link>,
      );
    }

    if (domainStatus === 'error') {
      actions.push(
        <a href={this.props.supportURL}>{__('contact the Customer Care')}</a>,
      );
    }

    // $FlowIgnore[incompatible-return]
    return actions;
  }

  render(): React.MixedElement {
    const { domainStatus, fqdn, getFallbackMessage } = this.props;
    const [primaryAction, secondaryAction] = this.renderActions();

    return (
      <CellRowGroup>
        <Cell tagName="div" flexGrow1>
          <Base tagName="p" flexGrow1 margin="none">
            <Fqdn maxLength={30} className={styles.fqdn}>
              {fqdn}
            </Fqdn>{' '}
            <Text color={domainStatus === 'unavailable' ? 'light' : null}>
              {getFallbackMessage(domainStatus)}
            </Text>
            {!!secondaryAction && <> {secondaryAction}</>}
          </Base>
        </Cell>
        {!!primaryAction && <Cell tagName="div">{primaryAction}</Cell>}
      </CellRowGroup>
    );
  }
}

const configURLs = createSelector<
  FullState,
  void,
  { supportURL: string, whoisURL: string },
  string,
  string,
  string,
>(
  [
    selectFromConfig<string>(
      'app.layout.urls.whois',
      'https://whois.gandi.net',
    ),
    selectHelpdeskUrl,
    localeSelector,
  ],
  (whoisURL, supportURL, locale) => ({
    supportURL,
    whoisURL: superpathjoin(whoisURL, locale, 'results'),
  }),
);

const mapStateToProps = (state: FullState, props: OwnProps) => ({
  ...configURLs(state),
  getFallbackMessage: memoizedFallbackMessages(props.translator.__),
});

export default (compose(
  withTranslator({ propsNamespace: 'translator' }),
  connect(mapStateToProps),
)(FallbackRow): React.ComponentType<Omit<OwnProps, 'translator'>>);
